import { action, makeObservable, observable, runInAction } from 'mobx';
import { AxiosError } from 'axios';

import { READER_TIMEZONE_HEADER } from 'core/constants';

import RootStore from 'stores/Root';
import { timezoneStore } from 'stores/Timezone';

import API from 'utils/api';
import { formErrors, isBadRequest } from 'utils/api/errors';
import { UserTimezoneSetting, UserTimezoneUpdate } from 'utils/api/modules/user/user.types';
import { APIRequest } from 'utils/api/types';
import Logger from 'utils/logger';

export type SettingStatus = 'loading' | 'ready' | 'failed';

export interface SettingState<T> {
  saved: T | null;
  draft: T | null;
  status: SettingStatus;
  saving: boolean;
  error: string | null;
}

const initialState = <T>(): SettingState<T> => ({
  saved: null,
  draft: null,
  status: 'loading',
  saving: false,
  error: null,
});

type Messages = { loadFailed: string; saved: string; saveFailed: string };

const PERSONAL: Messages = {
  loadFailed: 'Your timezone setting could not be loaded',
  saved: 'Timezone updated',
  saveFailed: 'Timezone could not be updated',
};

const ORGANISATION: Messages = {
  loadFailed: 'The organisation timezone could not be loaded',
  saved: 'Organisation timezone updated',
  saveFailed: 'Organisation timezone could not be updated',
};

class TimezoneSettingsStore {
  store: RootStore;

  api: typeof API;

  @observable personal: SettingState<UserTimezoneSetting> = initialState();

  @observable organisation: SettingState<string> = initialState();

  private generation = 0;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;
  }

  @action.bound
  loadPersonal(signal?: AbortSignal): Promise<void> {
    return this.load(
      this.personal,
      () => this.api.user.getTimezone(signal),
      (data) => data,
      PERSONAL,
      signal
    );
  }

  @action.bound
  loadOrganisation(signal?: AbortSignal): Promise<void> {
    return this.load(
      this.organisation,
      () => this.api.organisations.getTimezone(signal),
      ({ timezone }) => timezone,
      ORGANISATION,
      signal
    );
  }

  @action.bound
  savePersonal(update: UserTimezoneUpdate): Promise<void> {
    const draft = { timezone: null, ...this.personal.draft, ...update };
    return this.save(
      this.personal,
      draft,
      () => this.api.user.updateTimezone(update),
      (data) => data,
      PERSONAL
    );
  }

  @action.bound
  saveOrganisation(zone: string): Promise<void> {
    return this.save(
      this.organisation,
      zone,
      () => this.api.organisations.updateTimezone({ timezone: zone }),
      ({ timezone }) => timezone,
      ORGANISATION
    );
  }

  @action.bound
  reset(): void {
    this.generation += 1;
    this.personal = initialState();
    this.organisation = initialState();
  }

  private async load<T, R>(
    state: SettingState<T>,
    buildRequest: () => APIRequest<R>,
    read: (data: R) => T,
    messages: Messages,
    signal?: AbortSignal
  ): Promise<void> {
    if (state.saving) return;
    const { generation } = this;
    const firstLoad = state.saved === null;
    if (firstLoad) {
      runInAction(() => {
        state.status = 'loading';
      });
    }
    try {
      const { data } = await buildRequest()();
      if (generation !== this.generation) return;
      runInAction(() => {
        state.saved = read(data);
        state.draft = read(data);
        state.status = 'ready';
        state.error = null;
      });
    } catch (e) {
      if (signal?.aborted || generation !== this.generation) return;
      Logger.error(messages.loadFailed, e);
      if (firstLoad) {
        runInAction(() => {
          state.status = 'failed';
        });
      }
    }
  }

  // Never aborted: leaving the page must not lose the choice.
  private async save<T, R>(
    state: SettingState<T>,
    draft: T,
    buildRequest: () => APIRequest<R>,
    read: (data: R) => T,
    messages: Messages
  ): Promise<void> {
    if (state.saving) return;
    const { generation } = this;
    runInAction(() => {
      state.draft = draft;
      state.saving = true;
      state.error = null;
    });
    try {
      const response = await buildRequest()();
      if (generation !== this.generation) return;
      runInAction(() => {
        state.saved = read(response.data);
        state.draft = read(response.data);
      });
      timezoneStore.confirmZone(response.headers[READER_TIMEZONE_HEADER]);
      this.store.notification.enqueueSuccessSnackbar(messages.saved);
    } catch (e) {
      if (generation !== this.generation) return;
      const error = e as AxiosError;
      const message = isBadRequest(error) ? formErrors(error).fieldErrors?.timezone?.[0] : undefined;
      if (message) {
        runInAction(() => {
          state.error = message;
        });
      } else {
        Logger.error(messages.saveFailed, e);
        this.store.notification.enqueueErrorSnackbar(messages.saveFailed);
      }
    } finally {
      if (generation === this.generation) {
        runInAction(() => {
          state.saving = false;
        });
      }
    }
  }
}

export default TimezoneSettingsStore;
